/**
 * Voice AI — IVR Detail Panel
 * Header, tab bar, and tab routing for a single IVR menu.
 * Delegates all tab content to sub-components.
 * Dispatches: back, menu-updated, menu-deleted, toast
 * Pattern: agent-detail.js
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { ivrStyles } from '../../styles/ivr-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { ivrService } from '../../services/ivr-service.js';
import { confirmService } from '../../services/confirm-service.js';

import './ivr-menu-tab.js';
import './ivr-schedule-tab.js';
import './ivr-extensions-tab.js';
import './ivr-routes-tab.js';

export class IvrDetail extends LitElement {
  static properties = {
    menu: { type: Object },
    agents: { type: Array },
    saving: { type: Boolean },
    _activeTab: { type: String, state: true },
  };

  static styles = [designSystem, componentStyles, ivrStyles];

  constructor() {
    super();
    this.menu = null;
    this.agents = [];
    this.saving = false;
    this._activeTab = 'menu';
  }

  _setTab(tab) {
    this._activeTab = tab;
  }

  _back() {
    this.dispatchEvent(new CustomEvent('back'));
  }

  _toast(message) {
    this.dispatchEvent(new CustomEvent('toast', { detail: { message } }));
  }

  async _saveMenu(updatedMenu) {
    if (this.saving) return;
    this.saving = true;
    try {
      const body = ivrService.toSnakeCase(updatedMenu);
      const data = await ivrService.updateMenu(updatedMenu.id, body);
      if (data.success) {
        const transformed = ivrService.transformMenu(data.menu);
        this.menu = transformed;
        this.dispatchEvent(new CustomEvent('menu-updated', { detail: { menu: transformed } }));
        this._toast('IVR menu saved');
      } else {
        this._toast('Save failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('[IvrDetail] Save failed:', err);
      this._toast('Save failed: ' + err.message);
    } finally {
      this.saving = false;
    }
  }

  async _deleteMenu() {
    const confirmed = await confirmService.show({
      title: 'Delete IVR Menu',
      message: `Delete "${this.menu.name}"?`,
      description: 'This will also delete all options and schedules. This cannot be undone.',
    });
    if (!confirmed) return;
    try {
      const data = await ivrService.deleteMenu(this.menu.id);
      if (data.success) {
        this.dispatchEvent(new CustomEvent('menu-deleted', { detail: { id: this.menu.id } }));
        this._toast('IVR menu deleted');
      } else {
        this._toast('Delete failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      this._toast('Delete failed: ' + err.message);
    }
  }

  _handleTabSave(e) {
    this._saveMenu(e.detail.menu);
  }

  _handleMenuChange(e) {
    this.menu = e.detail.menu;
  }

  _statusBadge(status) {
    const map = {
      active: { cls: 'badge-success', dot: 'active', text: 'Active' },
      inactive: { cls: 'badge-neutral', dot: 'inactive', text: 'Inactive' },
      draft: { cls: 'badge-warning', dot: 'warning', text: 'Draft' },
    };
    const b = map[status] || map.draft;
    return html`
      <span class="badge ${b.cls}">
        <span class="status-dot ${b.dot}"></span>
        ${b.text}
      </span>
    `;
  }

  render() {
    if (!this.menu) return html``;
    const menu = this.menu;
    const TABS = [
      { key: 'menu', label: 'Menu Builder', icon: 'fa-list-tree' },
      { key: 'schedule', label: 'Schedules', icon: 'fa-calendar-clock' },
      { key: 'extensions', label: 'Extensions', icon: 'fa-phone-office' },
      { key: 'routes', label: 'Phone Routes', icon: 'fa-route' },
    ];

    return html`
      ${iconStyles}
      <div class="detail-panel">
        <button class="detail-back-btn" @click=${this._back}>
          <i class="fa-sharp fa-light fa-arrow-left"></i>
          Back to IVR Menus
        </button>

        <div class="detail-header">
          <div class="detail-avatar">
            <i class="fa-sharp fa-light fa-diagram-project"></i>
          </div>
          <div class="detail-title-area">
            <div class="detail-title">${menu.name}</div>
            <div class="detail-subtitle">
              ${this._statusBadge(menu.status)}
              ${menu.description || 'No description'}
            </div>
          </div>
        </div>

        <div class="detail-tabs">
          ${TABS.map(tab => html`
            <button
              class="detail-tab ${this._activeTab === tab.key ? 'active' : ''}"
              @click=${() => this._setTab(tab.key)}
            ><i class="fa-sharp fa-light ${tab.icon}"></i>${tab.label}</button>
          `)}
        </div>

        ${this._activeTab === 'menu' ? html`
          <ivr-menu-tab
            .menu=${menu}
            .agents=${this.agents}
            .saving=${this.saving}
            @save=${this._handleTabSave}
            @delete=${() => this._deleteMenu()}
            @menu-changed=${this._handleMenuChange}
            @toast=${(e) => this._toast(e.detail.message)}
          ></ivr-menu-tab>
        ` : ''}
        ${this._activeTab === 'schedule' ? html`
          <ivr-schedule-tab
            .menu=${menu}
            @toast=${(e) => this._toast(e.detail.message)}
          ></ivr-schedule-tab>
        ` : ''}
        ${this._activeTab === 'extensions' ? html`
          <ivr-extensions-tab
            .agents=${this.agents}
            @toast=${(e) => this._toast(e.detail.message)}
          ></ivr-extensions-tab>
        ` : ''}
        ${this._activeTab === 'routes' ? html`
          <ivr-routes-tab
            .menu=${menu}
            @toast=${(e) => this._toast(e.detail.message)}
          ></ivr-routes-tab>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('ivr-detail', IvrDetail);
